import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Keyboard as KeyboardIcon } from '@mui/icons-material';
import {
  SCAN_IDLE_MS,
  hidScanKeyAction,
  isOwnScanField,
  isTypingTarget,
  normalizeHidScanCode,
} from './hidScanCapture.mjs';

export default function TsdScanCapture({
  disabled = false,
  pause = false,
  capture = true,
  onScan,
  helperText = '',
  label = 'Ввести вручную',
  dialogTitle = 'Ручной ввод штрихкода',
  dialogPlaceholder = 'Штрихкод',
  compact = false,
  manualVariant = 'icon',
}) {
  const hiddenRef = useRef(null);
  const dialogInputRef = useRef(null);
  const bufferRef = useRef('');
  const idleRef = useRef(null);
  const onScanRef = useRef(onScan);
  const pauseRef = useRef(false);
  const disabledRef = useRef(disabled);
  const captureRef = useRef(capture);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualValue, setManualValue] = useState('');

  onScanRef.current = onScan;
  pauseRef.current = Boolean(pause) || manualOpen || disabled;
  disabledRef.current = disabled;
  captureRef.current = capture;

  const keepFocused = () => {
    if (!captureRef.current || pauseRef.current) return;
    const active = document.activeElement;
    if (active && active !== hiddenRef.current && isTypingTarget(active)) return;
    hiddenRef.current?.focus?.({ preventScroll: true });
  };

  const emit = (raw) => {
    const code = normalizeHidScanCode(raw);
    if (!code || disabledRef.current) return;
    onScanRef.current?.(code);
  };

  const clearBuffer = () => {
    bufferRef.current = '';
    if (hiddenRef.current) hiddenRef.current.value = '';
  };

  const flush = (value) => {
    if (idleRef.current) window.clearTimeout(idleRef.current);
    idleRef.current = null;
    const code = normalizeHidScanCode(value || bufferRef.current || '');
    clearBuffer();
    emit(code);
  };

  useEffect(() => {
    keepFocused();
    if (!capture) return undefined;
    const id = window.setInterval(keepFocused, 300);
    return () => window.clearInterval(id);
  }, [pause, disabled, manualOpen, capture]);

  useEffect(() => {
    if (!capture) return undefined;
    const onPointer = (event) => {
      const target = event.target;
      if (isTypingTarget(target)) return;
      if (target?.closest?.('.MuiPopover-root, .MuiMenu-root, .MuiSelect-root')) return;
      window.setTimeout(keepFocused, 0);
    };
    window.addEventListener('pointerdown', onPointer);
    return () => window.removeEventListener('pointerdown', onPointer);
  }, [capture]);

  useEffect(() => () => {
    if (idleRef.current) window.clearTimeout(idleRef.current);
  }, []);

  useEffect(() => {
    if (!capture) return undefined;

    const onKeyDown = (event) => {
      const result = hidScanKeyAction(event, {
        capture: captureRef.current,
        paused: pauseRef.current,
        ownField: isOwnScanField(event.target),
        typing: isTypingTarget(event.target),
        hasBuffer: Boolean(bufferRef.current),
      });
      if (result.preventDefault) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (result.action === 'ignore') return;
      if (result.action === 'append') {
        // Window-path: не даём hidden input копить «хвост» и затирать buffer через onChange.
        if (hiddenRef.current?.value) hiddenRef.current.value = '';
        bufferRef.current += result.char;
        if (idleRef.current) window.clearTimeout(idleRef.current);
        idleRef.current = window.setTimeout(() => flush(bufferRef.current), SCAN_IDLE_MS);
        return;
      }
      if (result.action === 'flush') flush(bufferRef.current);
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [capture]);

  const openManual = () => {
    if (disabled) return;
    if (idleRef.current) window.clearTimeout(idleRef.current);
    clearBuffer();
    setManualValue('');
    setManualOpen(true);
    window.setTimeout(() => dialogInputRef.current?.focus?.(), 80);
  };

  const closeManual = () => {
    setManualOpen(false);
  };

  const submitManual = () => {
    const code = normalizeHidScanCode(manualValue);
    if (!code) return;
    setManualOpen(false);
    setManualValue('');
    emit(code);
    window.setTimeout(keepFocused, 50);
  };

  const button = manualVariant === 'button' ? (
    <Button
      variant="outlined"
      size="large"
      fullWidth
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onFocus={() => { window.setTimeout(keepFocused, 0); }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') event.preventDefault();
      }}
      onClick={openManual}
      startIcon={<KeyboardIcon />}
      aria-label={label}
      tabIndex={-1}
      data-tsd-manual="1"
      sx={{ minHeight: 48 }}
    >
      {label}
    </Button>
  ) : (
    <Tooltip title={label}>
      <span>
        <IconButton
          size="small"
          color="primary"
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onFocus={() => { window.setTimeout(keepFocused, 0); }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') event.preventDefault();
          }}
          onClick={openManual}
          aria-label={label}
          tabIndex={-1}
          data-tsd-manual="1"
        >
          <KeyboardIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box
      data-tsd-scan-root="1"
      sx={{
        display: 'flex',
        alignItems: manualVariant === 'button' ? 'stretch' : 'center',
        flexDirection: manualVariant === 'button' ? 'column' : 'row',
        gap: manualVariant === 'button' ? 1 : 0.5,
        minWidth: 0,
      }}
    >
      {capture ? (
        <Box
          component="input"
          ref={hiddenRef}
          data-tsd-scan="1"
          aria-label="Сканер штрихкода"
          inputMode="none"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          disabled={disabled}
          onChange={(event) => {
            bufferRef.current = event.target.value;
            if (idleRef.current) window.clearTimeout(idleRef.current);
            idleRef.current = window.setTimeout(() => flush(bufferRef.current), SCAN_IDLE_MS);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            flush(event.target.value);
          }}
          onBlur={() => {
            window.setTimeout(keepFocused, 0);
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: 4,
            height: 4,
            opacity: 0,
            border: 0,
            padding: 0,
            margin: 0,
            zIndex: 1,
          }}
        />
      ) : null}
      {compact ? button : (
        <>
          {helperText ? (
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
              {helperText}
            </Typography>
          ) : null}
          {button}
        </>
      )}

      <Dialog
        open={manualOpen}
        onClose={() => { closeManual(); window.setTimeout(keepFocused, 50); }}
        fullWidth
        maxWidth="xs"
        data-tsd-manual="1"
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={dialogInputRef}
            autoFocus
            fullWidth
            margin="dense"
            label={dialogPlaceholder}
            value={manualValue}
            onChange={(event) => setManualValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submitManual();
              }
            }}
            autoComplete="off"
            inputProps={{
              'data-tsd-manual': '1',
              enterKeyHint: 'done',
              autoCorrect: 'off',
              autoCapitalize: 'off',
              spellCheck: false,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { closeManual(); window.setTimeout(keepFocused, 50); }}>Отмена</Button>
          <Button variant="contained" onClick={submitManual} disabled={!manualValue.trim()}>
            ОК
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
